/* eslint-disable @typescript-eslint/no-magic-numbers */
import { DestroyRef, effect, inject, Injectable, untracked } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AppointmentStatusCodeV2,
  ErrorCode,
  OperatoryAppointmentScheduleV2,
  OperatoryAppointmentsResponseV2,
  PatientAlertV2 as ApiPatientAlert,
  PatientAlertsDataService,
  PatientMedicalAlertsDataService,
  PmsType,
  PracticeOperatory,
  Provider,
  ProviderRole,
  ScheduleDataService,
  SitesDataService,
  SiteSettingsV2,
  UpdateAppointmentStatusV2,
  UsageEventTypeName,
  ProgressNoteV2,
} from '@ffd/api-client';
import { DateTime } from 'luxon';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ScheduleCalendarVM, ScheduleDetailsVM } from '@libs/schedule';
import { AutoPromptService } from '@libs/notes/auto-draft';
import {
  createAppointmentActions,
  filterMyDayAppointments,
  filterOperatoryAppointments,
  mapCreateAppointmentToOperatoryAppointment,
  mapCreateAppointmentToScheduleCalendarElement,
  mapMyDayAppointmentsWithEdit,
  mapOperatoryAppointmentsWithEdit,
  mapProviderColorToColor,
  ScheduleCalendarElement,
  ScheduleCalendarTimeWithOperatory,
  selectCurrentUserProfile,
  selectNewAppointment,
  ScheduleOperatoryProviderAppointment,
  selectEditedAppointment,
  selectDefaultAppointmentLength,
  selectTimeBlock,
  AppointmentActionType,
  mapMyDayAppointmentToScheduleCalendarElement,
  mapNewAppointmentToMyDayScheduleCalendarElement,
  selectUserSiteId,
  ScheduleOperatoryAppointment,
  mapOperatoryAppointmentToScheduleOperatoryAppointment,
  mapCreateAppointmentToCreateAppointmentRequest,
  mapCreateAppointmentToEditAppointmentRequest,
  DEFAULT_PRIMARY_COLOR,
  getAdjustedTimes,
  updateOperatoryAppointments,
  updateMyDayAppointments,
  EventDetailsVM,
  selectStatusOptions,
  selectHealthHistorySeverity,
  medicalAlertDefinitionActions,
  SelectProviderService,
  HealthHistoryStatus,
  filterMedicalAlertsToShow,
  filterPatientAlertsToShow,
  filterPatientAlertByDate,
  updateAppointmentInOperatories,
  updateAppointmentInMyDay,
  selectIsUserCollabOnlyAtSite,
  selectIsUserPatientAtSite,
  selectSiteSettings,
  authActions,
  healthHistoryActions,
  patientAlertsActions,
  selectShowcaseHealthHistoryCache,
  selectCurrentShowcasePatientId,
  selectShowcasePatientAlertsCache,
  HealthHistoryItem,
  UsageTrackingService,
} from '@libs/store';
import {
  adjustMinuteToTimeBlock,
  adjustTime,
  ApiHttpErrorResponse,
  DataState,
  DISABLE_GLOBAL_ERROR_TOASTS,
  FfdComponentStore,
  getAppointmentAssigneeName,
  getAppointmentAssigneeShortCode,
  getPmsDisplayCode,
  localStorageKey,
  routes,
  SKIP_CACHE,
} from '@libs/utils';
import { tapResponse, concatLatestFrom } from '@ngrx/operators';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { switchMap, tap, Observable, merge, of, EMPTY, filter, forkJoin, catchError, map, delay } from 'rxjs';
import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { OnboardingModalService, OnboardingTab } from '@libs/onboarding';
import { AppointmentPayload } from '@libs/fsa';
import { AlertActionButtonsService, MedicalAlert, medicalAlertLocalStorageId, PatientAlert, ToastService } from '@libs/shared-services';
import { DropdownItem } from '@libs/ui-lib';
import { NotesScreenService } from '@libs/notes';
import { PatientSelectorService } from '../patient-selector/patient-selector.service';

const APPOINTMENT_LENGTH_MULTIPLIER = 3;

export interface LocalOperatoryAppointmentSchedule extends Omit<OperatoryAppointmentScheduleV2, 'appointments'> {
  appointments: Array<OperatoryAppointmentWithEdit>;
}

// The Showcase schedule only uses the hour and minute of the stored time, read as local wall-clock time (see
// mapTimeStringToScheduleCalendarTime). The stored date is a fixed demo date and not the selected day. The prompts compare
// against the real clock, so they need the same reading of the time, placed on the selected date.
function toShowcaseLocalTimes(
  operatoryAppointments: LocalOperatoryAppointmentSchedule[],
  selectedDate: DateTime,
): LocalOperatoryAppointmentSchedule[] {
  const toSelectedDateIso = (time: string): string => {
    const { hour, minute } = DateTime.fromISO(time, { setZone: true });

    return selectedDate.set({ hour, minute, second: 0, millisecond: 0 }).toISO() ?? time;
  };

  return operatoryAppointments.map((operatory) => ({
    ...operatory,
    appointments: operatory.appointments.map((appointment) => ({
      ...appointment,
      from: toSelectedDateIso(appointment.from),
      to: toSelectedDateIso(appointment.to),
    })),
  }));
}

export interface OperatoryAppointmentWithEdit extends ScheduleOperatoryAppointment {
  isEdited?: boolean;
}

export interface SchedulesState {
  allOperatoryOptions: PracticeOperatory[];
  operatoryAppointments: LocalOperatoryAppointmentSchedule[];
  myDayAppointments: ScheduleOperatoryProviderAppointment[];
  internalProviders: Provider[];
  selectedDate: DateTime;
  selectedAppointmentId: string | null;
  selectedEventId: string | null;
  scheduleState: DataState;
  silentReloadState: DataState;
  showTentative: boolean;
  isCompactView: boolean;
  hidePatientName: boolean;
  saveInProgress: boolean;
  sidebarErrorMessage?: { title: string; message: string };
}

export interface SchedulesVM {
  siteId: string;
  myDay: ScheduleCalendarElement[];
  selectedOperatoryAppointments: ScheduleCalendarVM[];
  selectedDate: DateTime;
  selectedAppointment?: ScheduleDetailsVM;
  selectedEvent?: EventDetailsVM;
  operatoriesDropdownElements: DropdownItem<string>[];
  selectedOperatoryIds: string[];
  allProviders: Provider[];
  scheduleState: DataState;
  showTentative: boolean;
  isCompactView: boolean;
  hidePatientName: boolean;
  isToday: boolean;
  saveInProgress: boolean;
  sidebarErrorMessage?: { title: string; message: string };
}

interface ShowcaseScheduleCache {
  operatoryAppointments: LocalOperatoryAppointmentSchedule[];
  myDayAppointments: ScheduleOperatoryProviderAppointment[];
}

const initialState: SchedulesState = {
  allOperatoryOptions: [],
  operatoryAppointments: [],
  myDayAppointments: [],
  internalProviders: [],
  selectedDate: DateTime.now(),
  selectedAppointmentId: null,
  selectedEventId: null,
  scheduleState: DataState.Initial,
  silentReloadState: DataState.Initial,
  showTentative: JSON.parse(localStorage.getItem(localStorageKey.showTentativeAppointments) || 'true'),
  isCompactView: JSON.parse(localStorage.getItem(localStorageKey.isCompactView) || 'false'),
  hidePatientName: JSON.parse(localStorage.getItem(localStorageKey.hidePatientName) || 'false'),
  saveInProgress: false,
};

@Injectable({ providedIn: 'root' })
export class SchedulesService extends FfdComponentStore<SchedulesState, SchedulesVM> {
  private readonly route = inject(ActivatedRoute);
  private readonly sitesDataService = inject(SitesDataService);
  private readonly scheduleDataService = inject(ScheduleDataService);
  private readonly patientMedicalAlertsDataService = inject(PatientMedicalAlertsDataService);
  private readonly patientAlertsDataService = inject(PatientAlertsDataService);
  private readonly patientSelectorService = inject(PatientSelectorService);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly onboardingModalService = inject(OnboardingModalService);
  private readonly providerService = inject(SelectProviderService);
  private readonly usageTrackingService = inject(UsageTrackingService);
  private readonly notesScreenService = inject(NotesScreenService);
  public readonly allProviders = this.providerService.providers;
  private readonly alertActionButtonsService = inject(AlertActionButtonsService);
  private readonly autoPromptService = inject(AutoPromptService);
  private readonly actions$ = inject(Actions);
  private readonly showcaseCache = new Map<string, ShowcaseScheduleCache>();
  private isShowcaseInitPending = false;

  public readonly isScheduleLoadingSignal = this.selectSignal((s) => s.scheduleState === DataState.Loading);
  public readonly internalProvidersSignal = this.selectSignal((s) => s.internalProviders);
  private readonly operatoryAppointmentsSignal = this.selectSignal((s) => s.operatoryAppointments);
  private readonly selectedDateSignal = this.selectSignal((s) => s.selectedDate);
  private readonly currentUser = this.store.selectSignal(selectCurrentUserProfile);
  private readonly siteSettings = this.store.selectSignal(selectSiteSettings);
  /** Only Eaglesoft assigns appointments to staff and sends flat provider/staff lists - see getAppointmentAssigneeName. */
  private readonly isEaglesoftPms = (): boolean => this.siteSettings()?.pmsType === PmsType.EAGLESOFT;

  public readonly scheduleState$ = this.select((s) => s.scheduleState);

  private readonly siteId = this.store.selectSignal(selectUserSiteId);

  constructor() {
    super(initialState);

    effect(() => {
      const settings = this.siteSettings();

      if (settings) {
        this.setAppointmentSetting(settings);
      }
    });

    // The Start / Stop prompts follow the appointments the user sees. Feeding them from the schedule state (not from the raw
    // API response) also covers Showcase appointments that are created, edited or restored from the cache on the client (FW10-4386).
    effect(() => {
      const operatoryAppointments = this.operatoryAppointmentsSignal();
      const isShowcase = this.siteId() === routes.showcaseSiteId;
      const selectedDate = this.selectedDateSignal();

      untracked(() =>
        this.autoPromptService.updateAppointments(
          isShowcase ? toShowcaseLocalTimes(operatoryAppointments, selectedDate) : operatoryAppointments,
        ),
      );
    });

    this.initShowcaseCacheClearListeners();
    this.initShowcaseAlertSyncListener();
  }

  private initShowcaseCacheClearListeners(): void {
    this.actions$.pipe(ofType(authActions.onSiteSelected), takeUntilDestroyed(this.destroyRef)).subscribe(({ siteId }) => {
      if (siteId === routes.showcaseSiteId) {
        // The original Showcase appointments are not known yet, so the dismissed ids are cleaned when they load.
        this.isShowcaseInitPending = true;
      } else {
        this.showcaseCache.clear();
      }
    });

    this.actions$.pipe(ofType(authActions.onLogout), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.showcaseCache.clear();
    });
  }

  private initShowcaseAlertSyncListener(): void {
    this.actions$
      .pipe(
        ofType(
          healthHistoryActions.addItem,
          healthHistoryActions.updateItem,
          healthHistoryActions.deleteItem,
          patientAlertsActions.addItem,
          patientAlertsActions.updateItem,
          patientAlertsActions.deleteItem,
        ),
        concatLatestFrom(() => [
          this.store.select(selectCurrentShowcasePatientId),
          this.store.select(selectShowcaseHealthHistoryCache),
          this.store.select(selectShowcasePatientAlertsCache),
        ]),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([, patientId, healthHistoryCache, patientAlertsCache]) => {
        if (!patientId) return;
        const updatedMedicalAlerts = healthHistoryCache[patientId];
        const updatedPatientAlerts = patientAlertsCache[patientId];
        if (updatedMedicalAlerts === undefined && updatedPatientAlerts === undefined) return;
        this.updateShowcaseAppointmentAlerts(patientId, updatedMedicalAlerts, updatedPatientAlerts);
      });
  }

  private getShowcaseDateKey(): string {
    return this.state().selectedDate.toFormat('yyyy-MM-dd');
  }

  private saveToShowcaseCache(): void {
    const state = this.state();
    if (state.scheduleState !== DataState.Fetched) {
      return;
    }
    this.showcaseCache.set(this.getShowcaseDateKey(), {
      operatoryAppointments: state.operatoryAppointments,
      myDayAppointments: state.myDayAppointments,
    });
  }

  private getFromShowcaseCache(dateKey: string): ShowcaseScheduleCache | undefined {
    return this.showcaseCache.get(dateKey);
  }

  private updateShowcaseAppointmentAlerts(
    patientId: string,
    medicalAlerts: HealthHistoryItem[] | undefined,
    patientAlerts: ApiPatientAlert[] | undefined,
  ): void {
    const updateAppointment = (appointment: ScheduleOperatoryAppointment): ScheduleOperatoryAppointment => {
      if (appointment.patientId !== patientId) return appointment;
      return {
        ...appointment,
        ...(medicalAlerts !== undefined && {
          patientMedicalAlert: filterMedicalAlertsToShow(medicalAlerts).length > 0,
          medicalAlerts: medicalAlerts.length > 0 ? medicalAlerts : undefined,
        }),
        ...(patientAlerts !== undefined && {
          patientAlerts: patientAlerts.length > 0 ? patientAlerts : undefined,
        }),
      };
    };

    this.patchState((state) => ({
      operatoryAppointments: state.operatoryAppointments.map((operatory) => ({
        ...operatory,
        appointments: operatory.appointments.map((a) => updateAppointment(a)),
      })),
      myDayAppointments: state.myDayAppointments.map((entry) => ({
        ...entry,
        appointment: updateAppointment(entry.appointment),
      })),
    }));

    this.showcaseCache.forEach((cached, dateKey) => {
      this.showcaseCache.set(dateKey, {
        operatoryAppointments: cached.operatoryAppointments.map((operatory) => ({
          ...operatory,
          appointments: operatory.appointments.map((a) => updateAppointment(a)),
        })),
        myDayAppointments: cached.myDayAppointments.map((entry) => ({
          ...entry,
          appointment: updateAppointment(entry.appointment),
        })),
      });
    });
  }

  public reset(): void {
    this.patchState({
      allOperatoryOptions: [],
      operatoryAppointments: [],
      myDayAppointments: [],
      selectedDate: DateTime.now(),
      saveInProgress: false,
      sidebarErrorMessage: undefined,
    });
  }

  public override init(): void {
    this.patientSelectorService.init();
    this.setSelectedDateFromLocalStorage();
    this.onboardingModalService.setActiveTab(OnboardingTab.Schedule);
    this.alertActionButtonsService.init();
    this.usageTrackingService.trackUsage(UsageEventTypeName.SCHEDULE);
    this.fetchAppointments({});
  }

  public setDate(date: DateTime): void {
    if (this.siteId() === routes.showcaseSiteId) {
      this.saveToShowcaseCache();
    }

    this.saveSelectedDateToLocalStorage(date);
    this.patchState({ selectedDate: date });
    this.usageTrackingService.trackUsage(UsageEventTypeName.SCHEDULE);
    this.fetchAppointments({});
  }

  public readonly setAppointmentId = this.updater((state, appointmentId: string | null): SchedulesState => {
    const siteId = this.siteId() ?? routes.showcaseSiteId;
    const selectedOperatoryProviderAppointment = this.buildSelectedProviderAppointment(appointmentId);
    const patientId = selectedOperatoryProviderAppointment?.appointment?.patientId;

    this.store.dispatch(createAppointmentActions.onSetSelectedAppointment({ selectedAppointment: selectedOperatoryProviderAppointment }));
    if (patientId) {
      this.store.dispatch(
        medicalAlertDefinitionActions.getMedicalAlertDefinitions({
          siteId,
          patientId,
        }),
      );
    }

    if (appointmentId && !selectedOperatoryProviderAppointment?.appointment.isProvisional) {
      this.loadSelectedAppointmentProgressNotes(appointmentId);
    }

    return {
      ...state,
      selectedAppointmentId: appointmentId ?? null,
      selectedEventId: null,
    };
  });

  private readonly loadSelectedAppointmentProgressNotes = this.effect(($: Observable<string>) =>
    $.pipe(
      switchMap((appointmentId) => {
        const siteId = this.siteId();
        if (!siteId) {
          return EMPTY;
        }
        if (siteId === routes.showcaseSiteId) {
          const withNotes = this.getAppointmentDataWithProceduresForShowcase(appointmentId);
          if (withNotes?.appointment.progressNotes) {
            this.patchAppointmentProgressNotes({ appointmentId, progressNotes: withNotes.appointment.progressNotes });
            this.refreshSelectedAppointmentInStore(appointmentId);
          }
          return EMPTY;
        }
        return this.scheduleDataService
          .getAppointmentByIdV2(siteId, appointmentId, undefined, undefined, {
            context: new HttpContext().set(SKIP_CACHE, true),
          })
          .pipe(
            tapResponse(
              ({ appointment }) => {
                this.patchAppointmentProgressNotes({ appointmentId, progressNotes: appointment.progressNotes ?? [] });
                this.refreshSelectedAppointmentInStore(appointmentId);
              },
              (error: HttpErrorResponse) => console.error(error),
            ),
          );
      }),
    ),
  );

  private buildSelectedProviderAppointment(appointmentId: string | null): ScheduleOperatoryProviderAppointment | undefined {
    const currentDay = this.state().selectedDate.toFormat('yyyy-MM-dd');
    return this.state()
      .operatoryAppointments?.flatMap((operatory) =>
        operatory.appointments.map((a) => ({
          appointment: a,
          selectedDate: currentDay,
          operatoryName: operatory.operatoryName || operatory.id,
          operatoryId: operatory.id,
        })),
      )
      .find((app) => app.appointment.id === appointmentId);
  }

  private refreshSelectedAppointmentInStore(appointmentId: string): void {
    if (this.state().selectedAppointmentId !== appointmentId) {
      return;
    }
    const selectedOperatoryProviderAppointment = this.buildSelectedProviderAppointment(appointmentId);
    this.store.dispatch(createAppointmentActions.onSetSelectedAppointment({ selectedAppointment: selectedOperatoryProviderAppointment }));
  }

  private readonly patchAppointmentProgressNotes = this.updater(
    (state, { appointmentId, progressNotes }: { appointmentId: string; progressNotes: ProgressNoteV2[] }): SchedulesState => {
      const patch = (a: OperatoryAppointmentWithEdit): OperatoryAppointmentWithEdit =>
        a.id === appointmentId ? { ...a, progressNotes } : a;
      return {
        ...state,
        operatoryAppointments: state.operatoryAppointments.map((o) => ({ ...o, appointments: o.appointments.map((a) => patch(a)) })),
        myDayAppointments: state.myDayAppointments.map((e) => ({ ...e, appointment: patch(e.appointment) })),
      };
    },
  );

  public getAppointmentDataWithProcedures$(appointmentId: string): Observable<ScheduleOperatoryProviderAppointment | undefined> {
    const siteId = this.siteId();

    if (!siteId) {
      return EMPTY;
    }

    if (siteId === routes.showcaseSiteId) {
      return of(this.getAppointmentDataWithProceduresForShowcase(appointmentId));
    }

    return this.scheduleDataService
      .getAppointmentByIdV2(siteId, appointmentId, undefined, undefined, {
        context: new HttpContext().set(SKIP_CACHE, true),
      })
      .pipe(
        map(({ appointment }) => {
          const currentDay = this.state().selectedDate.toFormat('yyyy-MM-dd');
          const existingAppointment = this.state()
            .operatoryAppointments?.flatMap((operatory) =>
              operatory.appointments.map((a) => ({
                appointment: a,
                selectedDate: currentDay,
                operatoryName: operatory.operatoryName || operatory.id,
                operatoryId: operatory.id,
              })),
            )
            .find((app) => app.appointment.id === appointment.id);

          if (!existingAppointment) {
            return;
          }

          return {
            ...existingAppointment,
            appointment: {
              ...existingAppointment?.appointment,
              progressNotes: appointment.progressNotes,
            },
          } satisfies ScheduleOperatoryProviderAppointment;
        }),
      );
  }

  public getAppointmentDataWithProceduresForShowcase(appointmentId: string): ScheduleOperatoryProviderAppointment | undefined {
    const currentDay = this.state().selectedDate.toFormat('yyyy-MM-dd');
    return this.state()
      .operatoryAppointments?.flatMap((operatory) =>
        operatory.appointments.map((a) => ({
          appointment: a,
          selectedDate: currentDay,
          operatoryName: operatory.operatoryName || operatory.id,
          operatoryId: operatory.id,
        })),
      )
      .find(({ appointment }) => appointment.id === appointmentId);
  }

  public getFirstAppointmentIdByPatientId(patientId: string): string | null {
    const appointments = this.state().operatoryAppointments?.flatMap((operatory) => operatory.appointments) ?? [];
    const now = DateTime.now();

    const appointment = appointments
      .sort((a, b) => {
        const aDateTime = DateTime.fromISO(a.from);
        const bDateTime = DateTime.fromISO(b.from);
        return aDateTime > bDateTime ? 1 : -1;
      })
      .find((a) => {
        if (a.patientId !== patientId) return false;

        const appointmentDateTime = DateTime.fromISO(a.from);
        return appointmentDateTime > now;
      });

    return appointment?.id ?? null;
  }

  public readonly setEventId = this.updater((state, eventId: string): SchedulesState => {
    return {
      ...state,
      selectedAppointmentId: null,
      selectedEventId: eventId ?? null,
    };
  });

  public readonly setNewAppointmentWithCalendar = this.updater(
    (state, { hour, minute, operatory }: ScheduleCalendarTimeWithOperatory): SchedulesState => {
      const defaultAppointmentLength = this.store.selectSignal(selectDefaultAppointmentLength);

      this.store.dispatch(
        createAppointmentActions.onSetCreateAppointment({
          operatory,
          fromTime: adjustTime({ hour, minute }),
          toTime: adjustTime({ hour, minute: minute + defaultAppointmentLength() }),
          appointmentDate: state.selectedDate,
        }),
      );

      return {
        ...state,
        sidebarErrorMessage: undefined,
        selectedAppointmentId: null,
        selectedEventId: null,
      };
    },
  );

  public setNewAppointmentWithButton = this.updater((state): SchedulesState => {
    const defaultAppointmentLength = this.store.selectSignal(selectDefaultAppointmentLength);
    const timeBlock = this.store.selectSignal(selectTimeBlock);

    const date = new Date();
    const hour = date.getHours();
    const minute = adjustMinuteToTimeBlock({ minute: date.getMinutes(), timeBlock: timeBlock() });

    const firstOperatory = state.allOperatoryOptions[0];
    if (!firstOperatory) {
      return state;
    }

    const operatory = state.allOperatoryOptions.find((o) => o.id === firstOperatory.id) ?? state.allOperatoryOptions[0];

    this.store.dispatch(
      createAppointmentActions.onSetCreateAppointment({
        operatory,
        fromTime: adjustTime({ hour, minute }),
        toTime: adjustTime({ hour, minute: minute + defaultAppointmentLength() }),
        appointmentDate: state.selectedDate,
      }),
    );

    return { ...state, selectedAppointmentId: null, sidebarErrorMessage: undefined };
  });

  public readonly removeAppointmentSelection = this.updater(
    (state): SchedulesState => ({
      ...state,
      selectedAppointmentId: null,
      selectedEventId: null,
      sidebarErrorMessage: undefined,
    }),
  );

  public showTentative = this.updater((state, showTentative: boolean): SchedulesState => ({ ...state, showTentative }));
  public setShowTentative = (showTentative: boolean): void => {
    this.showTentative(showTentative);
    // According to the design: If users switches the tentative filter off once, then it stays up until they turn it on again
    localStorage.setItem(localStorageKey.showTentativeAppointments, JSON.stringify(showTentative));
  };
  public updateIsCompactView = this.updater((state, isCompactView: boolean): SchedulesState => ({ ...state, isCompactView }));
  public setIsCompactView = (isCompactView: boolean): void => {
    this.updateIsCompactView(isCompactView);
    localStorage.setItem(localStorageKey.isCompactView, JSON.stringify(isCompactView));
  };

  public updateHidePatientName = this.updater((state, hidePatientName: boolean): SchedulesState => ({ ...state, hidePatientName }));
  public setHidePatientName = (hidePatientName: boolean): void => {
    this.updateHidePatientName(hidePatientName);
    localStorage.setItem(localStorageKey.hidePatientName, JSON.stringify(hidePatientName));
  };

  public readonly setSelectedOperatoryIds = ({ selectedOperatoryIds }: { selectedOperatoryIds: string[] }): void => {
    this.store.dispatch(authActions.updateSelectedOperatoryIds({ selectedOperatoryIds }));
    this.saveSelectedOperatoryIds({ selectedOperatoryIds });
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  protected override mapStateToViewModel(state: SchedulesState): SchedulesVM {
    const currentDay = this.state().selectedDate.toFormat('yyyy-MM-dd');
    const allAppointments = [
      ...(state.myDayAppointments ?? []),
      ...(state.operatoryAppointments?.flatMap((o) =>
        o.appointments.map(
          (a) =>
            ({
              appointment: a,
              selectedDate: currentDay,
              operatoryName: o.operatoryName || o.id,
              operatoryId: o.id,
            }) satisfies ScheduleOperatoryProviderAppointment,
        ),
      ) ?? []),
    ];

    const newAppointmentState = this.store.selectSignal(selectNewAppointment)();

    const selectedAppointment = allAppointments.find((a) => a.appointment.id === state.selectedAppointmentId);
    const siteId = this.siteId() ?? routes.showcaseSiteId;

    //if you add a new appointment, it should show up in my day
    const isShowcase = siteId === routes.showcaseSiteId;
    const currentUserSite = this.currentUser()?.sites.find((site) => site.id === siteId);
    const currentProviderId = currentUserSite?.provider?.id;
    const isEaglesoftPms = this.isEaglesoftPms();
    let myDay = state.myDayAppointments?.flatMap((a) =>
      mapMyDayAppointmentToScheduleCalendarElement(a, isShowcase, isEaglesoftPms, {
        providerId: currentProviderId,
        staffId: currentUserSite?.staff?.id,
      }),
    );

    if (newAppointmentState.appointmentActionType === AppointmentActionType.Edit) {
      myDay = myDay?.filter((a) => a.id !== newAppointmentState.id);
    }
    if (newAppointmentState.selectedProvider?.id === currentProviderId) {
      myDay?.push(...mapNewAppointmentToMyDayScheduleCalendarElement(newAppointmentState));
    }

    //the visible operatories can be set in a dropdown, we need to show only the selected ones
    const siteSettingsSelectedIds = this.siteSettings()?.selectedOperatoryIds;
    const effectiveSelectedIds = siteSettingsSelectedIds ?? state.allOperatoryOptions.map((o) => o.id);
    const selectedOperatoryAppointments = state.allOperatoryOptions
      .filter((o) => effectiveSelectedIds.includes(o.id))
      .map((operatory) => {
        const operatoryAppointments = state.operatoryAppointments?.find((o) => o.id === operatory.id);
        const appointments =
          operatoryAppointments?.appointments.map((a) =>
            this.mapOperatoryAppointmentToScheduleCalendarElement({
              appointment: a,
              selectedDate: currentDay,
              operatoryName: operatory.name,
              operatoryId: operatory.id,
            }),
          ) ?? [];

        const exceptions =
          operatoryAppointments?.exceptions.map((e) => {
            return getAdjustedTimes(e.from, e.to, isShowcase, currentDay);
          }) ?? [];

        const events =
          operatoryAppointments?.events?.map((e) => ({
            id: e.id,
            title: e.description || '',
            date: e.from || currentDay,
            from: getAdjustedTimes(e.from, e.to, isShowcase, currentDay).from,
            to: getAdjustedTimes(e.from, e.to, isShowcase, currentDay).to,
            operatoryName: operatoryAppointments.operatoryName || e.operatoryId,
            color: e.color,
            notes: e.notes,
          })) ?? [];

        const shouldShowNewAppointment = newAppointmentState && operatory.id === newAppointmentState.operatory?.id;

        //we also want to show the new appointment in the UI
        const newAppointment = shouldShowNewAppointment
          ? mapCreateAppointmentToScheduleCalendarElement(
              newAppointmentState,
              state.saveInProgress || state.silentReloadState === DataState.Loading,
            )
          : undefined;

        let newAppointmentWithPreRegPatient: ScheduleCalendarElement | undefined;
        if (newAppointment && selectedAppointment && selectedAppointment.appointment.isPreRegistrationAppointment) {
          newAppointmentWithPreRegPatient = {
            ...newAppointment,
            isPreRegistrationAppointment: true,
            patientName: `${selectedAppointment?.appointment.preRegistrationPatientName} ${selectedAppointment?.appointment.preRegistrationPatientSurname}`,
          };
        }

        return {
          id: operatory.id,
          operatoryName: operatory.name || operatory.id,
          appointments,
          exceptions,
          events,
          newAppointment: selectedAppointment?.appointment.isPreRegistrationAppointment ? newAppointmentWithPreRegPatient : newAppointment,
        };
      });

    const allEvents = selectedOperatoryAppointments.flatMap((o) => o.events);

    return {
      myDay,
      selectedOperatoryAppointments: selectedOperatoryAppointments,
      selectedDate: state.selectedDate,
      selectedAppointment: selectedAppointment
        ? this.mapOperatoryAppointmentToScheduleDetails(selectedAppointment, siteId, isShowcase)
        : undefined,
      selectedEvent: this.getSelectedEvent(state.selectedEventId, allEvents),
      operatoriesDropdownElements: state.allOperatoryOptions.map((o) => ({ label: o.name || o.id, value: o.id })),
      selectedOperatoryIds: effectiveSelectedIds.filter((id) => state.allOperatoryOptions.some((o) => o.id === id)),
      allProviders: state.internalProviders,
      scheduleState: state.scheduleState,
      showTentative: state.showTentative,
      isCompactView: state.isCompactView,
      hidePatientName: state.hidePatientName,
      siteId,
      isToday: state.selectedDate.toFormat('yyyy-MM-dd') === DateTime.now().toFormat('yyyy-MM-dd'),
      saveInProgress: state.saveInProgress,
      sidebarErrorMessage: state.sidebarErrorMessage,
    };
  }

  private getSelectedEvent(selectedEventId: string | null, allEvents: Array<EventDetailsVM>): EventDetailsVM | undefined {
    return selectedEventId ? allEvents.find((e) => e.id === selectedEventId) : undefined;
  }

  private readonly setSaveInProgress = this.updater((state, saveInProgress: boolean): SchedulesState => {
    return {
      ...state,
      saveInProgress: saveInProgress,
    };
  });

  private readonly setSidebarErrorMessage = this.updater(
    (state, sidebarErrorMessage: { title: string; message: string } | undefined): SchedulesState => {
      return {
        ...state,
        sidebarErrorMessage,
      };
    },
  );

  private readonly resetSidebarErrorMessage = this.updater((state): SchedulesState => {
    return {
      ...state,
      sidebarErrorMessage: undefined,
    };
  });

  private mapOperatoryAppointmentToScheduleCalendarElement(
    providerAppointment: ScheduleOperatoryProviderAppointment,
  ): ScheduleCalendarElement {
    const siteId = this.siteId();
    const isShowcase = siteId === routes.showcaseSiteId;
    const { selectedDate, operatoryName, operatoryId, appointment } = providerAppointment;
    const { from, to } = getAdjustedTimes(appointment.from, appointment.to, isShowcase, selectedDate);
    return {
      id: appointment.id,
      from,
      to,
      date: selectedDate,
      timePattern: appointment.timePattern,
      color: appointment.provider ? mapProviderColorToColor(appointment.provider.color) : DEFAULT_PRIMARY_COLOR,
      patientName: appointment.isPreRegistrationAppointment
        ? `${appointment.preRegistrationPatientName} ${appointment.preRegistrationPatientSurname}`
        : `${appointment.patientName} ${appointment.patientSurname}`,
      patientId: appointment.patientId ?? '',
      hasHealthHistory: appointment.patientMedicalAlert,
      providerName: getAppointmentAssigneeName(appointment, this.isEaglesoftPms()),
      pmsProviderId: getAppointmentAssigneeShortCode(appointment, this.isEaglesoftPms()),
      secondaryProviderName: appointment.additionalProvider
        ? `${appointment.additionalProvider.firstName} ${appointment.additionalProvider.lastName}`
        : undefined,
      secondaryPmsProviderId: appointment.additionalProvider ? getPmsDisplayCode(appointment.additionalProvider) : undefined,
      secondaryColor: appointment.additionalProvider
        ? mapProviderColorToColor(appointment.additionalProvider.color)
        : DEFAULT_PRIMARY_COLOR,
      reason: appointment.reason,
      operatoryName: operatoryName || operatoryId,
      isTentative: appointment.draft,
      isEdited: appointment.isEdited,
      isProvisional: appointment.isProvisional,
      isPreRegistrationAppointment: appointment.isPreRegistrationAppointment,
      statusId: appointment.statusId,
      statusColor: appointment.statusColor,
      statusDisplayName: appointment.statusDisplayName,
      notes: appointment.notes ?? '',
      medicalAlerts: filterMedicalAlertsToShow(appointment.medicalAlerts).map((alert) => alert.description),
      patientAlerts: filterPatientAlertsToShow(appointment.patientAlerts).map((alert) => alert.description ?? ''),
      siteId: siteId ?? '',
    };
  }

  private mapOperatoryAppointmentToScheduleDetails(
    providerAppointment: ScheduleOperatoryProviderAppointment,
    siteId: string,
    isShowcase: boolean,
  ): ScheduleDetailsVM {
    const { selectedDate, operatoryName, operatoryId, appointment } = providerAppointment;
    const statusOptions = this.store.selectSignal(selectStatusOptions)();
    const healthHistorySeverity = this.store.selectSignal(selectHealthHistorySeverity)();

    const criticalHealthHistoryItems =
      appointment.medicalAlerts
        ?.filter((item) => item.isCritical && item.status === HealthHistoryStatus.Active)
        .map((item) => item.description) ?? [];

    const medicalAlertItem =
      criticalHealthHistoryItems.length > 0
        ? ({
            patientId: appointment.patientId ?? '',
            localStorageId: medicalAlertLocalStorageId,
            type: 'medical',
            alerts: criticalHealthHistoryItems,
          } as MedicalAlert)
        : undefined;

    const patientAlertItems =
      appointment.patientAlerts
        ?.filter((item) => item.showInSchedule && filterPatientAlertByDate(item))
        .map(
          (item) =>
            ({
              patientId: appointment.patientId ?? '',
              localStorageId: item.id.toString(),
              type: 'patient',
              description: item.description,
              notes: item.noteText,
            }) as PatientAlert,
        ) ?? [];

    return {
      id: appointment.id,
      person: {
        firstName: appointment.isPreRegistrationAppointment ? appointment.preRegistrationPatientName : appointment.patientName,
        lastName: appointment.isPreRegistrationAppointment ? appointment.preRegistrationPatientSurname : appointment.patientSurname,
      },
      siteId,
      patientId: appointment.patientId,
      phoneNumber: appointment.patientPhone ?? '',
      healthHistorySeverity,
      medicalAlertItem,
      patientAlertItems,
      appointmentDetails: {
        reason: appointment.reason,
        date: selectedDate,
        time: getAdjustedTimes(appointment.from, appointment.to, isShowcase, selectedDate),
        operatoryname: operatoryName || operatoryId,
        provider: getAppointmentAssigneeName(appointment, this.isEaglesoftPms()),
        status: statusOptions?.find((s) => s.id === appointment.statusId)?.description ?? 'None',
        statusColor: appointment.statusColor,
        statusDisplayName: appointment.statusDisplayName,
        notes: appointment.notes ?? '',
        additionalProvider: appointment.additionalProvider
          ? `${appointment.additionalProvider.firstName} ${appointment.additionalProvider.lastName}`
          : undefined,
        staff: appointment.staffName,
        isTentative: appointment.draft,
        isProvisional: appointment.isProvisional,
        isPreRegistrationAppointment: appointment.isPreRegistrationAppointment,
      },
    };
  }

  public readonly fetchAppointments = this.effect(
    (
      $: Observable<{
        silentReload?: boolean;
        provisionalId?: string;
        newAppointmentId?: string;
        skipCache?: boolean;
        callback?: () => void;
      }>,
    ) =>
      $.pipe(
        concatLatestFrom(() => [
          this.select((s) => ({ selectedDate: s.selectedDate })),
          this.store.select(selectIsUserCollabOnlyAtSite),
          this.store.select(selectIsUserPatientAtSite),
        ]),
        filter(([_, __, isCollabOnly, isPatient]) => {
          return isCollabOnly !== undefined && !isCollabOnly && !isPatient;
        }),
        tap(([effect]) => {
          if (effect.silentReload) {
            this.patchState({ silentReloadState: DataState.Loading });
          } else {
            this.patchState({ scheduleState: DataState.Loading });
          }
        }),
        // eslint-disable-next-line sonarjs/cognitive-complexity
        switchMap(([{ provisionalId, newAppointmentId, skipCache, callback }, date]) => {
          const siteId = this.siteId();
          const dateInIso = date.selectedDate.startOf('day').toISO() ?? '';
          const baseContext = new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true);

          // Create two separate HTTP contexts for the cached and non-cached requests
          const cachedContext = baseContext;
          const nonCachedContext = baseContext.set(SKIP_CACHE, true);

          if (skipCache) {
            // If skipCache is explicitly requested, we'll just use a non-cached request.
            // We need to make a separate request without medical alerts as well so the schedules are loaded faster.
            const requestWithoutMedicalAlerts$ = this.scheduleDataService
              .getScheduleBySiteV2(siteId ?? routes.showcaseSiteId, dateInIso, undefined, true, false, undefined, undefined, {
                context: nonCachedContext,
              })
              .pipe(this.fetchScheduleResponseHandler(siteId ?? routes.showcaseSiteId, provisionalId, newAppointmentId, date, callback));

            const requestWithMedicalAlerts$ = this.scheduleDataService
              .getScheduleBySiteV2(siteId ?? routes.showcaseSiteId, dateInIso, undefined, false, false, undefined, undefined, {
                context: nonCachedContext,
              })
              .pipe(this.fetchScheduleResponseHandler(siteId ?? routes.showcaseSiteId, provisionalId, newAppointmentId, date, callback));

            return merge(requestWithoutMedicalAlerts$, requestWithMedicalAlerts$);
          }

          // First we need to make a request with cache excluding medical alerts.
          return this.scheduleDataService
            .getScheduleBySiteV2(siteId ?? routes.showcaseSiteId, dateInIso, undefined, false, true, 'response', undefined, {
              context: cachedContext,
            })
            .pipe(
              switchMap(({ status, body }) => {
                // If the cached response is No Content (204) we need to call the request again non cached.
                // We need to make a separate request without medical alerts as well so the schedules are loaded faster.
                if (status === 204) {
                  const requestWithoutMedicalAlerts$ = this.scheduleDataService
                    .getScheduleBySiteV2(siteId ?? routes.showcaseSiteId, dateInIso, undefined, true, false, undefined, undefined, {
                      context: nonCachedContext,
                    })
                    .pipe(
                      this.fetchScheduleResponseHandler(siteId ?? routes.showcaseSiteId, provisionalId, newAppointmentId, date, callback),
                    );

                  const requestWithMedicalAlerts$ = this.scheduleDataService
                    .getScheduleBySiteV2(siteId ?? routes.showcaseSiteId, dateInIso, undefined, false, false, undefined, undefined, {
                      context: nonCachedContext,
                    })
                    .pipe(
                      this.fetchScheduleResponseHandler(siteId ?? routes.showcaseSiteId, provisionalId, newAppointmentId, date, callback),
                    );

                  return merge(requestWithoutMedicalAlerts$, requestWithMedicalAlerts$);
                }

                // Cache hit: process the cached response and also load medical alerts.
                const cachedResponse$ = body
                  ? of(body).pipe(
                      this.fetchScheduleResponseHandler(siteId ?? routes.showcaseSiteId, provisionalId, newAppointmentId, date, callback),
                    )
                  : EMPTY;

                const requestWithMedicalAlerts$ = this.scheduleDataService
                  .getScheduleBySiteV2(siteId ?? routes.showcaseSiteId, dateInIso, undefined, false, false, undefined, undefined, {
                    context: nonCachedContext,
                  })
                  .pipe(
                    this.fetchScheduleResponseHandler(siteId ?? routes.showcaseSiteId, provisionalId, newAppointmentId, date, callback),
                  );

                return merge(cachedResponse$, requestWithMedicalAlerts$);
              }),
            );
        }),
      ),
  );

  private fetchScheduleResponseHandler(
    siteId: string,
    provisionalId?: string,
    newAppointmentId?: string,
    date?: { selectedDate: DateTime },
    callback?: () => void,
  ) {
    // eslint-disable-next-line sonarjs/cognitive-complexity
    return (source$: Observable<OperatoryAppointmentsResponseV2>) =>
      source$.pipe(
        tapResponse({
          next: (response) => {
            if (!date || !response) return;

            this.patientSelectorService.setDefaultSelectedPatient();

            const operatories = response.operatories.map((o) => ({
              id: o.id,
              name: o.name || o.id,
            }));

            const currentDay = date.selectedDate.toFormat('yyyy-MM-dd');
            const siteId = this.siteId();
            const isShowcase = siteId === routes.showcaseSiteId;

            // mock the actual provider in showcase so we can see the appointments in my day
            const operatoryAppointments = response.operatoryAppointments.map((appointment) => ({
              ...appointment,
              appointments: appointment.appointments.map((a) => {
                // We keep the newly created appointment's id to keep the selection after fetching
                if (a.id === provisionalId && newAppointmentId) {
                  a.id = newAppointmentId;
                }
                return mapOperatoryAppointmentToScheduleOperatoryAppointment(a);
              }),
            }));

            const myDayAppointments = response.myDay.map((appointment) => ({
              ...appointment,
              appointment: mapOperatoryAppointmentToScheduleOperatoryAppointment(appointment.appointment),
              selectedDate: currentDay,
            }));

            // show all internal providers
            const internalProviders = this.allProviders().data?.filter((p) => p.role === ProviderRole.INTERNAL) || [];

            // For showcase, restore cached modifications if available for this date
            if (isShowcase) {
              // Showcase data starts from scratch, so the Start prompts that were dismissed in an earlier session can show again.
              // Only the Showcase appointment ids are removed, because the same list holds the ids of the real sites.
              if (this.isShowcaseInitPending) {
                this.isShowcaseInitPending = false;
                this.autoPromptService.clearDismissedShowcasePrompts(
                  response.operatoryAppointments.flatMap(({ appointments }) => appointments.map(({ id }) => id)),
                );
              }

              const cached = this.getFromShowcaseCache(currentDay);
              const healthHistoryCache = this.store.selectSignal(selectShowcaseHealthHistoryCache)();
              const patientAlertsCache = this.store.selectSignal(selectShowcasePatientAlertsCache)();
              const enrichFromCache = (appointment: ScheduleOperatoryAppointment): ScheduleOperatoryAppointment => {
                const cachedMedicalAlerts = appointment.patientId ? healthHistoryCache[appointment.patientId] : undefined;
                const cachedPatientAlerts = appointment.patientId ? patientAlertsCache[appointment.patientId] : undefined;
                if (!cachedMedicalAlerts && !cachedPatientAlerts) return appointment;
                return {
                  ...appointment,
                  ...(cachedMedicalAlerts !== undefined && {
                    patientMedicalAlert: filterMedicalAlertsToShow(cachedMedicalAlerts).length > 0,
                    medicalAlerts: cachedMedicalAlerts.length > 0 ? cachedMedicalAlerts : undefined,
                  }),
                  ...(cachedPatientAlerts !== undefined && {
                    patientAlerts: cachedPatientAlerts.length > 0 ? cachedPatientAlerts : undefined,
                  }),
                };
              };

              if (cached) {
                this.patchState({
                  allOperatoryOptions: operatories,
                  operatoryAppointments: cached.operatoryAppointments.map((operatory) => ({
                    ...operatory,
                    appointments: operatory.appointments.map((a) => enrichFromCache(a)),
                  })),
                  myDayAppointments: cached.myDayAppointments.map((entry) => ({
                    ...entry,
                    appointment: enrichFromCache(entry.appointment),
                  })),
                  internalProviders,
                  scheduleState: DataState.Fetched,
                  silentReloadState: DataState.Fetched,
                });
                this.store.dispatch(createAppointmentActions.onSetAllOperatories({ allOperatories: operatories }));
                callback?.();
                return;
              }

              const enrichedOperatoryAppointments = operatoryAppointments.map((operatory) => ({
                ...operatory,
                appointments: operatory.appointments.map((a) => enrichFromCache(a)),
              }));
              const enrichedMyDayAppointments = myDayAppointments.map((entry) => ({
                ...entry,
                appointment: enrichFromCache(entry.appointment),
              }));
              this.patchState({
                allOperatoryOptions: operatories,
                operatoryAppointments: enrichedOperatoryAppointments,
                myDayAppointments: enrichedMyDayAppointments,
                internalProviders,
                scheduleState: DataState.Fetched,
                silentReloadState: DataState.Fetched,
              });
              this.store.dispatch(createAppointmentActions.onSetAllOperatories({ allOperatories: operatories }));
              callback?.();
              return;
            }

            this.patchState({
              allOperatoryOptions: operatories,
              operatoryAppointments,
              myDayAppointments,
              internalProviders,
              scheduleState: DataState.Fetched,
              silentReloadState: DataState.Fetched,
            });

            this.store.dispatch(createAppointmentActions.onSetAllOperatories({ allOperatories: operatories }));
            callback?.();
          },
          error: (error: HttpErrorResponse) => {
            // Only show error and reset state if both requests fail
            // If this is the second request that failed (after a successful one), don't show error
            if (this.state().scheduleState !== DataState.Fetched) {
              this.toastService.error(this.translateService.instant('error_message_server_error_schedule_by_site'));
              this.patchState({
                allOperatoryOptions: [],
                operatoryAppointments: [],
                myDayAppointments: [],
                internalProviders: [],
                scheduleState: DataState.Error,
                silentReloadState: DataState.Error,
              });
            }
            return console.error(error);
          },
        }),
      );
  }

  /**
   * Optimistically inserts the appointment currently being created as a provisional entry into the
   * existing operatory and my-day appointments. This lets the schedule reflect the new appointment
   * immediately after a successful create, instead of keeping the sidebar open until the (potentially
   * slow, non-cached) silent reload finishes. The provisional entry shares the local appointment id,
   * so the reloaded appointment replaces it without creating a duplicate.
   */
  public readonly addProvisionalAppointment = this.updater((state, { isTentative }: { isTentative: boolean }): SchedulesState => {
    const newAppointment = this.store.selectSignal(selectNewAppointment)();
    const siteId = this.siteId();
    const currentProvider = this.currentUser()?.sites.find((site) => site.id === siteId)?.provider;
    const operatoryAppointment = mapCreateAppointmentToOperatoryAppointment({ ...newAppointment, isTentative });

    return {
      ...state,
      operatoryAppointments: updateOperatoryAppointments(state, operatoryAppointment, newAppointment.operatory),
      myDayAppointments: updateMyDayAppointments(state, operatoryAppointment, currentProvider, newAppointment.operatory) ?? [],
    };
  });

  private readonly createAppointment = this.effect(($: Observable<{ isTentative: boolean; isHidden?: boolean }>) =>
    $.pipe(
      switchMap((action) => {
        const siteId = this.siteId() ?? routes.showcaseSiteId;
        const newAppointment = this.store.selectSignal(selectNewAppointment)();

        const createdAppointment = mapCreateAppointmentToCreateAppointmentRequest({
          ...newAppointment,
          isTentative: action.isTentative,
        });

        this.resetSidebarErrorMessage();
        this.setSaveInProgress(true);

        return this.scheduleDataService
          .createAppointmentV2(siteId, createdAppointment, undefined, undefined, {
            context: new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true),
          })
          .pipe(
            tapResponse(
              ({ provisionalId }) => {
                this.showSaveSuccessModal(
                  { type: 'create', isTentative: action.isTentative, isHidden: action.isHidden },
                  newAppointment.id,
                );
                if (newAppointment.patient?.id) {
                  this.notesScreenService.resetNotes();
                  this.notesScreenService.setPatientIdAndSiteId({ patientId: newAppointment.patient.id, siteId });
                  this.notesScreenService.init();
                }

                // Show the appointment immediately as provisional and free the sidebar, instead of
                // waiting for the slow non-cached reload below to finish (FW10-2695).
                this.addProvisionalAppointment({ isTentative: action.isTentative });
                this.setSaveInProgress(false);
                this.store.dispatch(createAppointmentActions.onResetCreateAppointment());

                // Reconcile with the server in the background. The provisional entry is replaced once
                // the appointments are loaded, and the refresh no longer closes a sidebar the user may
                // have reopened to create another appointment in the meantime.
                this.fetchAppointments({
                  silentReload: true,
                  provisionalId,
                  newAppointmentId: newAppointment.id,
                  skipCache: true,
                });
              },
              (error: ApiHttpErrorResponse) => {
                this.setSaveInProgress(false);
                if (error.status === 400 && error.error?.code === ErrorCode.ALREADY_ASSOCIATED) {
                  this.setSidebarErrorMessage({
                    title: this.translateService.instant('error_message_generic_schedule_create_appointment_title'),
                    message: 'One of the procedure codes are already associated with an other appointment',
                  });
                } else {
                  this.setSidebarErrorMessage({
                    title: this.translateService.instant('error_message_generic_schedule_create_appointment_title'),
                    message: this.translateService.instant('error_message_generic_schedule_appointment_content'),
                  });
                }
                this.editSelectedAppointment({ isEdited: true });
                console.error(error);
              },
            ),
          );
      }),
    ),
  );

  public deleteRealAppointment = this.effect(($: Observable<string>) =>
    $.pipe(
      switchMap((appointmentId) => {
        const siteId = this.siteId() ?? routes.showcaseSiteId;
        return this.scheduleDataService.deleteAppointmentV2(siteId, appointmentId).pipe(
          tapResponse(
            () => {
              this.toastService.info(this.translateService.instant('schedule_delete_appointment_modal_delete_success'));
            },
            (error: HttpErrorResponse) => {
              this.toastService.error(this.translateService.instant('error_message_server_error_delete_appointment'));
              return console.error(error);
            },
          ),
        );
      }),
    ),
  );

  public readonly editAppointment = this.effect(($: Observable<{ isTentative: boolean; isHidden?: boolean }>) =>
    $.pipe(
      switchMap((action) => {
        const siteId = this.siteId() ?? routes.showcaseSiteId;
        const appointment = this.store.selectSignal(selectNewAppointment)();

        const updatedAppointment = mapCreateAppointmentToEditAppointmentRequest({
          ...appointment,
          isTentative: action.isTentative,
        });
        this.setSaveInProgress(true);

        return this.scheduleDataService
          .updateAppointmentV2(siteId, appointment.id, updatedAppointment, undefined, undefined, {
            context: new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true),
          })
          .pipe(
            tapResponse(
              () => {
                this.showSaveSuccessModal({ type: 'edit', isTentative: action.isTentative, isHidden: action.isHidden }, appointment.id);

                // Apply the edit immediately as a provisional update and free the sidebar, instead of
                // waiting for the slow non-cached reload below to finish (FW10-2695).
                this.addProvisionalAppointment({ isTentative: action.isTentative });
                this.setSaveInProgress(false);
                this.store.dispatch(createAppointmentActions.onResetCreateAppointment());
                this.removeAppointmentSelection();

                // Reconcile with the server in the background. The provisional entry shares the
                // appointment id, so the reloaded appointment replaces it without creating a duplicate.
                this.fetchAppointments({
                  silentReload: true,
                  skipCache: true,
                });
              },
              (error: HttpErrorResponse) => {
                this.setSaveInProgress(false);
                this.setSidebarErrorMessage({
                  title: this.translateService.instant('error_message_generic_schedule_update_appointment_title'),
                  message: this.translateService.instant('error_message_generic_schedule_appointment_content'),
                });
                console.error(error);
              },
            ),
          );
      }),
    ),
  );

  private showSaveSuccessModal(
    payload: { type: 'create' | 'edit'; isTentative: boolean; isHidden?: boolean },
    operatoryAppointmentId: string,
  ): void {
    let messageKey = 'schedule_create_appointment_success';

    if (payload.isHidden) {
      messageKey = 'create_appointment_toast_created_hidden';
    }

    if (payload.isHidden && payload.isTentative) {
      messageKey = 'create_appointment_toast_created_tentative_hidden';
    }

    if (payload.isTentative && !payload.isHidden) {
      this.setShowTentative(true);
      messageKey = 'schedule_tentative_appointment_success';
    }

    if (payload.type === 'edit') {
      messageKey = 'schedule_modify_appointment_success';
    }

    this.toastService
      .success(
        this.translateService.instant(messageKey),
        this.translateService.instant('schedule_create_appointment_success_action_button'),
      )
      .actionClicked$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.setAppointmentId(operatoryAppointmentId));
  }

  public readonly saveSelectedOperatoryIds = this.effect(($: Observable<{ selectedOperatoryIds?: string[] }>) =>
    $.pipe(
      switchMap(({ selectedOperatoryIds }) => {
        const siteId = this.siteId() ?? routes.showcaseSiteId;
        return this.sitesDataService
          .updateSelectedOperatories(siteId, { selectedOperatoryIds }, undefined, undefined, {
            context: new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true),
          })
          .pipe(
            tapResponse(
              () => {
                if (siteId !== routes.showcaseSiteId) {
                  this.store.dispatch(authActions.getSiteSettings({ siteId }));
                }
              },
              (error: HttpErrorResponse) => {
                return console.error(error);
              },
            ),
          );
      }),
    ),
  );

  private saveShowcaseAppointment(
    operatoryAppointment: ScheduleOperatoryAppointment,
    payload: { type: 'create' | 'edit'; isTentative: boolean; notes: string },
    isAppointmentVisible: boolean,
    targetOperatory: PracticeOperatory | undefined,
  ): void {
    const state = this.state();
    const siteId = this.siteId();
    const currentProvider = this.currentUser()?.sites.find((site) => site.id === siteId)?.provider;

    this.patchState({
      operatoryAppointments: updateOperatoryAppointments(state, operatoryAppointment, targetOperatory),
      myDayAppointments: updateMyDayAppointments(state, operatoryAppointment, currentProvider, targetOperatory) ?? [],
      selectedAppointmentId: undefined,
    });
    this.saveToShowcaseCache();
    this.showSaveSuccessModal({ ...payload, isHidden: !isAppointmentVisible }, operatoryAppointment.id);
    this.store.dispatch(createAppointmentActions.onResetCreateAppointment());
  }

  public saveAppointment = (payload: { type: 'create' | 'edit'; isTentative: boolean; notes: string }): void => {
    this.store.dispatch(createAppointmentActions.onSetNotes({ notes: payload.notes }));

    const newAppointment = this.store.selectSignal(selectNewAppointment)();
    const savedOperatoryIds = this.siteSettings()?.selectedOperatoryIds;
    const isAppointmentVisible = savedOperatoryIds?.includes(newAppointment.operatory?.id ?? '');
    const operatoryId = newAppointment.operatory?.id ?? '';
    if (!savedOperatoryIds?.includes(operatoryId) && savedOperatoryIds) {
      this.setSelectedOperatoryIds({ selectedOperatoryIds: [...(savedOperatoryIds ?? []), operatoryId] });
    }

    const siteId = this.siteId();
    const operatoryAppointment = mapCreateAppointmentToOperatoryAppointment({ ...newAppointment, isTentative: payload.isTentative });

    if (siteId === routes.showcaseSiteId) {
      this.saveShowcaseAppointmentByType(
        operatoryAppointment,
        payload,
        newAppointment.patient?.id,
        isAppointmentVisible ?? false,
        newAppointment.operatory,
      );
    } else {
      if (payload.type === 'create') {
        this.createAppointment({ isTentative: payload.isTentative, isHidden: !isAppointmentVisible });
      } else {
        this.editAppointment({ isTentative: payload.isTentative, isHidden: !isAppointmentVisible });
      }
    }
  };

  private saveShowcaseAppointmentByType(
    operatoryAppointment: ScheduleOperatoryAppointment,
    payload: { type: 'create' | 'edit'; isTentative: boolean; notes: string },
    patientId: string | undefined,
    isAppointmentVisible: boolean,
    targetOperatory: PracticeOperatory | undefined,
  ): void {
    if (payload.type === 'create' && patientId) {
      forkJoin({
        medicalAlerts$: this.patientMedicalAlertsDataService
          .getMedicalAlerts(routes.showcaseSiteId, patientId)
          .pipe(catchError(() => of([]))),
        patientAlerts$: this.patientAlertsDataService.getPatientAlertsV2(routes.showcaseSiteId, patientId).pipe(catchError(() => of([]))),
      })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(({ medicalAlerts$: medicalAlerts, patientAlerts$: patientAlerts }) => {
          const cachedHealthHistory = this.store.selectSignal(selectShowcaseHealthHistoryCache)();
          const enrichedMedicalAlerts = cachedHealthHistory[patientId] ?? medicalAlerts;
          const cachedPatientAlerts = this.store.selectSignal(selectShowcasePatientAlertsCache)();
          const enrichedPatientAlerts = cachedPatientAlerts[patientId] ?? patientAlerts;

          const enriched: ScheduleOperatoryAppointment = {
            ...operatoryAppointment,
            patientMedicalAlert: filterMedicalAlertsToShow(enrichedMedicalAlerts).length > 0,
            medicalAlerts: enrichedMedicalAlerts.length > 0 ? enrichedMedicalAlerts : undefined,
            patientAlerts: enrichedPatientAlerts.length > 0 ? enrichedPatientAlerts : undefined,
          };
          this.saveShowcaseAppointment(enriched, payload, isAppointmentVisible, targetOperatory);
        });
    } else {
      this.saveShowcaseAppointment(operatoryAppointment, payload, isAppointmentVisible, targetOperatory);
    }
  }

  private readonly deleteAppointmentUpdater = this.updater((state): SchedulesState => {
    const selectedAppointment = this.store.selectSignal(selectEditedAppointment)()!;

    this.store.dispatch(createAppointmentActions.onResetCreateAppointment());

    return {
      ...state,
      operatoryAppointments: filterOperatoryAppointments(state, selectedAppointment.operatoryId, selectedAppointment.appointment.id),
      myDayAppointments: filterMyDayAppointments(state, selectedAppointment.appointment.id) ?? [],
      selectedAppointmentId: null,
    };
  });

  public deleteAppointment = (): void => {
    const selectedAppointment = this.store.selectSignal(selectEditedAppointment)();
    this.deleteAppointmentUpdater();
    const siteId = this.siteId();
    if (siteId === routes.showcaseSiteId) {
      this.saveToShowcaseCache();
    } else if (selectedAppointment) {
      this.deleteRealAppointment(selectedAppointment.appointment.id);
    }
  };

  public editSelectedAppointment = this.updater((state, payload: { isEdited: boolean }): SchedulesState => {
    const selectedAppointment = this.store.selectSignal(selectEditedAppointment)();

    if (!selectedAppointment) return { ...state };

    const operatoryAppointments = mapOperatoryAppointmentsWithEdit(
      state,
      selectedAppointment.operatoryId,
      selectedAppointment.appointment.id,
      payload.isEdited,
    );
    const myDayAppointments = mapMyDayAppointmentsWithEdit(state, selectedAppointment.appointment.id, payload.isEdited);

    return {
      ...state,
      operatoryAppointments,
      myDayAppointments: myDayAppointments ?? [],
    };
  });

  private readonly getSelectedDateStorageKey = (): string => {
    const siteId = this.siteId() ?? routes.showcaseSiteId;
    return `${localStorageKey.lastVisitedScheduleDate}_${this.currentUser()?.id}_${siteId}`;
  };

  private readonly setSelectedDateFromLocalStorage = (): void => {
    const localStorageDate = localStorage.getItem(this.getSelectedDateStorageKey());
    if (localStorageDate) {
      this.patchState({
        selectedDate: DateTime.fromISO(JSON.parse(localStorageDate)),
      });
    }
  };

  private readonly saveSelectedDateToLocalStorage = (date: DateTime): void => {
    localStorage.setItem(this.getSelectedDateStorageKey(), JSON.stringify(date));
  };

  public handleSocketUpdate(data: AppointmentPayload): void {
    const siteId = this.siteId();
    if (data.siteId === siteId) {
      this.fetchAppointments({ silentReload: true });
    }
  }

  public readonly updateAppointmentStatusEffect = this.effect(
    (
      $: Observable<{
        appointmentId: string;
        status: AppointmentStatusCodeV2;
        isTentative: boolean;
        originalStatus?: AppointmentStatusCodeV2;
      }>,
    ) =>
      $.pipe(
        switchMap((action) => {
          const siteId = this.siteId() ?? routes.showcaseSiteId;

          // Use the new simplified UpdateAppointmentStatus model
          const updateStatusRequest: UpdateAppointmentStatusV2 = {
            statusId: action.status.id,
          };

          this.setSaveInProgress(true);

          // Update appointment status and set provisional immediately for instant visual feedback
          this.setAppointmentProvisional({ appointmentId: action.appointmentId, isProvisional: true, status: action.status });

          // For showcase site, handle status update client-side only
          if (siteId === routes.showcaseSiteId) {
            return of(EMPTY).pipe(
              delay(200),
              tap(() => {
                this.setSaveInProgress(false);
                this.saveToShowcaseCache();
                this.showSaveSuccessModal({ type: 'edit', isTentative: action.isTentative, isHidden: false }, action.appointmentId);
                this.store.dispatch(createAppointmentActions.onSetStatus({ status: action.status }));
              }),
            );
          }

          return this.scheduleDataService
            .setAppointmentStatusV2(siteId, action.appointmentId, updateStatusRequest, undefined, undefined, {
              context: new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true),
            })
            .pipe(
              tapResponse(
                () => {
                  // Show success modal for status update using existing pattern
                  this.showSaveSuccessModal({ type: 'edit', isTentative: action.isTentative, isHidden: false }, action.appointmentId);

                  this.fetchAppointments({
                    silentReload: true,
                    skipCache: true,
                    callback: () => {
                      this.setSaveInProgress(false);
                    },
                  });
                  this.store.dispatch(createAppointmentActions.onSetStatus({ status: action.status }));
                },
                (error: HttpErrorResponse) => {
                  this.setSaveInProgress(false);
                  this.toastService.error(this.translateService.instant('error_message_server_error_update_appointment_status'));

                  // Revert to original status on failure
                  if (action.originalStatus) {
                    this.setAppointmentProvisional({
                      appointmentId: action.appointmentId,
                      isProvisional: false,
                      status: action.originalStatus,
                    });
                  }
                  console.error(error);
                },
              ),
            );
        }),
      ),
  );

  public updateAppointmentStatus(
    appointmentId: string,
    status: AppointmentStatusCodeV2,
    isTentative: boolean,
    originalStatus?: AppointmentStatusCodeV2,
  ): void {
    this.updateAppointmentStatusEffect({ appointmentId, status, isTentative, originalStatus });
  }

  private readonly setAppointmentProvisional = this.updater(
    (
      state: SchedulesState,
      { appointmentId, isProvisional, status }: { appointmentId: string; isProvisional: boolean; status?: AppointmentStatusCodeV2 },
    ): SchedulesState => {
      const appointmentUpdates: Partial<OperatoryAppointmentWithEdit> = {
        isProvisional,
        ...(status && { statusId: status.id, statusColor: status.statusColor }),
      };

      const operatoryAppointments = updateAppointmentInOperatories(state.operatoryAppointments, appointmentId, appointmentUpdates);

      const myDayAppointments = updateAppointmentInMyDay(state.myDayAppointments, appointmentId, appointmentUpdates);

      return { ...state, operatoryAppointments, myDayAppointments };
    },
  );

  private setAppointmentSetting(siteSettings: SiteSettingsV2): void {
    // eslint-disable-next-line @ngrx/avoid-dispatching-multiple-actions-sequentially
    this.store.dispatch(
      createAppointmentActions.onSetAppointmentUnitSize({
        defaultAppointmentLength: siteSettings.appointmentUnitSizeInMin * APPOINTMENT_LENGTH_MULTIPLIER,
        timeBlock: siteSettings.appointmentUnitSizeInMin,
      }),
    );

    // eslint-disable-next-line @ngrx/avoid-dispatching-multiple-actions-sequentially
    this.store.dispatch(createAppointmentActions.onSetStatusOptions({ options: siteSettings.appointmentStatusCodes ?? [] }));
  }
}
